import { z } from "zod";
import type { UserProfileCreate } from "@bw/shared/types/generated";

/**
 * The create body the onboarding form produces — the generated `UserProfileCreate`
 * (ADR-005, the real backend contract) with `visibility` made optional.
 *
 * `visibility` is a server-defaulted field (`= "public"` on the Pydantic model);
 * openapi-typescript emits Pydantic defaults as REQUIRED, but the wire contract
 * accepts the POST without it and the server owns the default. The form
 * deliberately does NOT surface `visibility`, so the body it builds omits it.
 * Derived from the generated source (not a hand-written mirror) so it tracks any
 * backend schema change.
 */
export type ProfileCreateBody = Omit<UserProfileCreate, "visibility"> & {
  // Keep `visibility` as the generated literal type (not a widened `string`), just
  // made optional — so the body matches `UserApi.createProfile` exactly with no
  // boundary cast (#1311). The form still omits it; the server owns the default.
  visibility?: UserProfileCreate["visibility"];
};

/**
 * Onboarding profile Zod schema (player Slice 2).
 *
 * Mirrors the REAL backend contract `UserProfileCreate` (common/models.py:1178;
 * the generated `@bw/shared` alias) and enforces ONLY the same plausibility
 * bounds the backend enforces, plus the sign-up requirement to choose a gender.
 *
 *   REQUIRED
 *     - display_name : non-empty (trimmed)
 *     - date_of_birth: ISO "YYYY-MM-DD"; NOT in the future; NOT > 120 years ago
 *                      (mirrors the backend field_validator on date_of_birth)
 *     - gender : ProfileGender — required at sign-up (event eligibility); the
 *                server locks it once set
 *   OPTIONAL
 *     - play_formats / skill_level : how they play (shown on the profile)
 *     - home_area : a COARSE area from the area picker (sent as home_*)
 *     - avatar : https URL (the backend validator requires it to start "https://")
 *     - bio    : free text
 *
 * `visibility` defaults to "public" on the backend and is NOT surfaced in the
 * form, so it is intentionally absent here — the server owns the default.
 *
 * COPPA: there is deliberately NO client-side age gate beyond the plausibility
 * bounds. The under-13 / under-18 decision is computed SERVER-SIDE from
 * date_of_birth (_compute_age_status) and read back off the response. A valid
 * under-13 DOB MUST parse — that user is allowed to submit; the backend returns
 * account_status="pending_parental_consent".
 */

// The four real ProfileGender enum values — nothing invented.
export const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
] as const;

const MAX_AGE_YEARS = 120;

/** Today's date at local midnight — the upper bound for a plausible DOB. */
function todayAtMidnight(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** The earliest plausible DOB: MAX_AGE_YEARS before today. */
function earliestPlausibleDob(): Date {
  const t = todayAtMidnight();
  return new Date(t.getFullYear() - MAX_AGE_YEARS, t.getMonth(), t.getDate());
}

const displayName = z
  .string()
  .trim()
  .min(1, "Enter a display name.");

const dateOfBirth = z
  .string()
  .min(1, "Enter your date of birth.")
  // Must be a real ISO calendar date string.
  .refine((v) => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v)), {
    message: "Enter a valid date of birth.",
  })
  // NOT in the future (mirrors the backend "no future DOB" rule).
  .refine(
    (v) => {
      const d = new Date(`${v}T00:00:00`);
      return d.getTime() <= todayAtMidnight().getTime();
    },
    { message: "Date of birth can't be in the future." },
  )
  // NOT more than 120 years ago (mirrors the backend ceiling).
  .refine(
    (v) => {
      const d = new Date(`${v}T00:00:00`);
      return d.getTime() >= earliestPlausibleDob().getTime();
    },
    { message: "Enter a valid date of birth (within the last 120 years)." },
  );

// Required at sign-up: it decides men's, women's and mixed event eligibility,
// and it can't be changed afterwards (support corrects it).
const gender = z.enum(["male", "female", "other", "prefer_not_to_say"], {
  message: "Choose one so we can place you in the right events.",
});

const playFormats = z.array(z.enum(["singles", "doubles", "mixed"])).optional();
const skillLevel = z.enum(["beginner", "intermediate", "advanced"]).optional();
const homeArea = z
  .object({ label: z.string(), lat: z.number(), lng: z.number() })
  .optional();

// Optional avatar. An EMPTY string (an untouched <input>) is treated as
// "omitted" and passes; the https rule only applies once the user enters a
// value. Keeping the field a plain `string | undefined` (no z.preprocess) keeps
// the schema's input and output types aligned so RHF's resolver types cleanly.
const avatar = z
  .string()
  .refine(
    (s) => s === "" || (isUrl(s) && s.startsWith("https://")),
    { message: "Avatar URL must start with https://." },
  )
  .optional();

// Optional bio — any free text, including empty.
const bio = z.string().optional();

function isUrl(s: string): boolean {
  try {
    // eslint-disable-next-line no-new
    new URL(s);
    return true;
  } catch {
    return false;
  }
}

/**
 * profileSchema — only real `UserProfileCreate` keys (home_area maps to the
 * home_* fields). `.strip()` (the Zod default for objects) drops any unknown
 * key, so an invented client field can never reach the backend.
 */
export const profileSchema = z.object({
  display_name: displayName,
  date_of_birth: dateOfBirth,
  gender,
  play_formats: playFormats,
  skill_level: skillLevel,
  home_area: homeArea,
  avatar,
  bio,
});

export type ProfileFormValues = z.infer<typeof profileSchema>;
